import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, HelpCircle, FileText, Search, Award, AlertTriangle, CheckCircle } from 'lucide-react';
import { SEO } from '../components/SEO';
import { LazyImage } from '../components/LazyImage';
import {
  generateArticleStructuredData,
  generateFAQStructuredData,
  generateHowToStructuredData,
  generateBreadcrumbStructuredData
} from '../utils/structuredData';
import { useEffect } from 'react';

export function AlltOmStiftelser() {
  // Handle anchor links scrolling with offset for fixed header
  useEffect(() => {
    // Check if there's a hash in the URL
    if (window.location.hash) {
      // Get the target element
      const id = window.location.hash.substring(1);
      const element = document.getElementById(id);
      
      if (element) {
        // Add a slight delay to ensure the page is fully loaded
        setTimeout(() => {
          const yOffset = -80; // Adjust based on your header height
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 300);
      }
    }
  }, []);
  
  // Function to scroll back to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // Define breadcrumbs for the page
  const breadcrumbs = [
    { name: 'Hem', url: 'https://www.sokastiftelsemedel.se/' },
    { name: 'Allt Om Stiftelser', url: 'https://www.sokastiftelsemedel.se/allt-om-stiftelser' }
  ];

  // Define HowTo steps for "Att söka stiftelsemedel"
  const howToSteps = [
    {
      name: "Förberedelser innan ansökan",
      text: "Förstå stiftelsens ändamål, kartlägg relevanta stiftelser, och samla nödvändig dokumentation."
    },
    {
      name: "Utforma en övertygande ansökan",
      text: "Förbered en tydlig ansökan, strukturera den väl, var realistisk med belopp, och använd en personlig ton."
    },
    {
      name: "Inlämning och uppföljning",
      text: "Respektera deadlines och formkrav, skicka in ansökan i god tid, följ upp din ansökan, och sök från flera källor."
    }
  ];

  return (
    <main className="max-w-4xl mx-auto px-4 py-8">
      <SEO
        title="Söka Stiftelsemedel: Komplett Guide till Svenska Stiftelser"
        description="Lär dig hur du kan söka stiftelsemedel och stiftelsekapital effektivt. Vår databas med över 17 000 svenska stiftelser hjälper dig hitta rätt finansiering för ditt ändamål."
        keywords="söka stiftelsemedel, söka stiftelse kapital, hur söker man stiftelsekapital, kan man söka stiftelsekapital, stiftelser, svenska stiftelser, stiftelselagen, starta stiftelse, avkastningsstiftelser, verksamhetsstiftelser, insamlingsstiftelser"
        canonicalUrl="https://www.sokastiftelsemedel.se/allt-om-stiftelser"
        structuredData={{
          article: generateArticleStructuredData(
            "Söka Stiftelsemedel: Komplett Guide till Svenska Stiftelser",
            "Lär dig hur du kan söka stiftelsemedel och stiftelsekapital effektivt. Vår databas med över 17 000 svenska stiftelser hjälper dig hitta rätt finansiering för ditt ändamål.",
            "https://www.sokastiftelsemedel.se/allt-om-stiftelser",
            "https://www.sokastiftelsemedel.se/images/stiftelser-guide.jpg",
            "2025-04-02",
            "2025-04-02"
          ),
          faq: generateFAQStructuredData([
            {
              question: "Hur söker man stiftelsekapital?",
              answer: "För att söka stiftelsekapital behöver du först identifiera stiftelser vars ändamål matchar ditt behov. Använd SOKASTIFTELSEMEDEL.SE för att hitta relevanta stiftelser. Läs sedan noga igenom stiftelsens stadgar och ansökningskrav. Förbered en tydlig ansökan som beskriver ditt syfte, hur det matchar stiftelsens ändamål, och en detaljerad budget. Skicka in ansökan enligt stiftelsens instruktioner och inom angiven deadline."
            },
            {
              question: "Kan man söka stiftelsekapital som privatperson?",
              answer: "Ja, många stiftelser riktar sig specifikt till privatpersoner. Det finns stiftelser som stödjer allt från studier och forskning till kulturella projekt och sociala behov. Som privatperson kan du söka medel för exempelvis utbildning, forskning, kulturella projekt, eller vid ekonomiska svårigheter. Avgörande är att ditt behov matchar stiftelsens ändamål, vilket framgår av stiftelsens stadgar."
            },
            {
              question: "Vad krävs för att söka stiftelsemedel?",
              answer: "För att söka stiftelsemedel krävs vanligtvis att du uppfyller stiftelsens kriterier (ålder, bostadsort, studieinriktning, etc.), en komplett ansökan enligt stiftelsens instruktioner, relevanta bilagor som personbevis, intyg, projektbeskrivning och budget, att du ansöker inom angiven ansökningsperiod, samt en tydlig koppling mellan ditt behov och stiftelsens ändamål."
            },
            {
              question: "Hur lång tid tar det att få svar på en stiftelseansökan?",
              answer: "Handläggningstiden varierar kraftigt mellan olika stiftelser. Mindre stiftelser kan ge besked inom några veckor, medan större stiftelser med många ansökningar kan ta 3-6 månader eller längre. Vissa stiftelser har fasta utdelningstillfällen, exempelvis en eller två gånger per år, vilket påverkar när du kan förvänta dig svar."
            },
            {
              question: "Måste man betala tillbaka stiftelsemedel?",
              answer: "Nej, stiftelsemedel är vanligtvis bidrag eller stipendier som inte behöver återbetalas. Till skillnad från lån är stiftelsemedel gåvor utan återbetalningskrav. Däremot kan stiftelsen ställa krav på hur medlen används och att du redovisar hur pengarna har använts."
            }
          ]),
          howTo: generateHowToStructuredData(
            "Att söka stiftelsemedel",
            "En komplett guide till hur du effektivt söker stiftelsemedel från svenska stiftelser.",
            "https://www.sokastiftelsemedel.se/images/stiftelser-guide.jpg",
            howToSteps,
            "P1D" // Estimated time: 1 day
          ),
          breadcrumb: generateBreadcrumbStructuredData(breadcrumbs)
        }}
      />
      <article className="bg-white rounded-lg shadow-md p-6 mb-8">
        <header>
          <h1 className="text-3xl font-bold text-[#2563eb] mb-6">Söka Stiftelsemedel: Komplett Guide till Svenska Stiftelser</h1>
        </header>
        
        {/* Table of Contents */}
        <nav className="bg-[#F8FAFC] p-4 rounded-lg border border-[#E2E8F0] mb-8" id="top" aria-label="Table of Contents">
          <h2 className="text-xl font-semibold mb-3 text-[#2563eb]">Innehåll</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <li><a href="#vad-ar-en-stiftelse" className="text-[#2563eb] hover:underline flex items-center gap-1"><FileText className="w-4 h-4 flex-shrink-0" /> <span>Vad är en stiftelse?</span></a></li>
            <li><a href="#olika-typer" className="text-[#2563eb] hover:underline flex items-center gap-1"><FileText className="w-4 h-4 flex-shrink-0" /> <span>Olika typer av stiftelser</span></a></li>
            <li><a href="#juridiska-ramar" className="text-[#2563eb] hover:underline flex items-center gap-1"><FileText className="w-4 h-4 flex-shrink-0" /> <span>Stiftelselagen och juridiska ramar</span></a></li>
            <li><a href="#starta-stiftelse" className="text-[#2563eb] hover:underline flex items-center gap-1"><FileText className="w-4 h-4 flex-shrink-0" /> <span>Att starta en stiftelse</span></a></li>
            <li><a href="#soka-stiftelsemedel" className="text-[#2563eb] hover:underline flex items-center gap-1"><Search className="w-4 h-4 flex-shrink-0" /> <span>Att söka stiftelsemedel</span></a></li>
            <li><a href="#vanliga-misstag" className="text-[#2563eb] hover:underline flex items-center gap-1"><AlertTriangle className="w-4 h-4 flex-shrink-0" /> <span>Vanliga misstag att undvika</span></a></li>
            <li><a href="#framgangsrika-ansokningar" className="text-[#2563eb] hover:underline flex items-center gap-1"><CheckCircle className="w-4 h-4 flex-shrink-0" /> <span>Framgångsrika ansökningar</span></a></li>
            <li><a href="#sokastiftelsemedel" className="text-[#2563eb] hover:underline flex items-center gap-1"><Award className="w-4 h-4 flex-shrink-0" /> <span>Hur SOKASTIFTELSEMEDEL.SE kan hjälpa dig</span></a></li>
            <li><a href="#framtiden" className="text-[#2563eb] hover:underline flex items-center gap-1"><FileText className="w-4 h-4 flex-shrink-0" /> <span>Framtiden för stiftelser i Sverige</span></a></li>
            <li><a href="#vanliga-fragor" className="text-[#2563eb] hover:underline flex items-center gap-1"><HelpCircle className="w-4 h-4 flex-shrink-0" /> <span>Vanliga frågor (FAQ)</span></a></li>
          </ul>
        </nav>
        
        <section id="vad-ar-en-stiftelse" className="mb-8 scroll-mt-24">
          <h2 className="text-2xl font-semibold mb-3 text-[#A3C1DA]">Vad är en stiftelse?</h2>
          <p className="mb-4">
            En stiftelse är en självägande juridisk person som förvaltar en förmögenhet för ett bestämt ändamål. 
            Till skillnad från föreningar och bolag har stiftelser inga medlemmar eller ägare. Stiftelser bildas 
            när en eller flera stiftare avsätter egendom för att varaktigt tjäna ett bestämt ändamål.
          </p>
          <p className="mb-4">
            I Sverige regleras stiftelser främst av stiftelselagen (1994:1220) som trädde i kraft den 1 januari 1996. 
            Denna lag definierar vad en stiftelse är, hur den bildas, styrs och avvecklas. Stiftelser står under tillsyn 
            av länsstyrelserna och måste följa särskilda regler för redovisning och förvaltning.
          </p>
          <p>
            Stiftelser spelar en viktig roll i det svenska samhället genom att finansiera forskning, utbildning, 
            kultur, sociala insatser och mycket mer. De bidrar årligen med miljardbelopp till olika ändamål och 
            utgör ett viktigt komplement till offentlig finansiering.
          </p>
          <div className="mt-4 text-right">
            <button onClick={scrollToTop} className="inline-flex items-center text-[#2563eb] hover:underline">
              <span className="mr-1">Tillbaka till toppen</span>
              <ArrowLeft className="w-4 h-4 transform rotate-90" />
            </button>
          </div>
        </section>
        
        <section id="olika-typer" className="mb-8 scroll-mt-24">
          <h2 className="text-2xl font-semibold mb-3 text-[#A3C1DA]">Olika typer av stiftelser</h2>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Avkastningsstiftelser</h3>
            <p>
              Den vanligaste typen av stiftelse i Sverige. Dessa stiftelser delar ut bidrag från avkastningen på sitt kapital. 
              Kapitalet i sig ska bevaras intakt över tid. Exempel är stipendiestiftelser och forskningsstiftelser.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Verksamhetsstiftelser</h3>
            <p>
              Bedriver egen verksamhet för att uppfylla sitt ändamål, till exempel sjukhus, museer eller skolor. 
              Dessa stiftelser kan förbruka både avkastning och kapital för att finansiera sin verksamhet.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Insamlingsstiftelser</h3>
            <p>
              Bildas för att samla in pengar till ett visst ändamål. De har ofta ett litet grundkapital men 
              förlitar sig på kontinuerliga donationer. Många välgörenhetsorganisationer är organiserade som insamlingsstiftelser.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Familjestiftelser</h3>
            <p>
              Har till syfte att gynna medlemmar av en viss släkt eller familj. Dessa stiftelser har särskilda 
              skatteregler och begränsningar. Sedan 1996 kan nya familjestiftelser endast bildas under särskilda omständigheter.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Näringsdrivande stiftelser</h3>
            <p>
              Bedriver näringsverksamhet, antingen direkt eller genom att äga bolag. Dessa stiftelser 
              kombinerar ofta näringsverksamhet med allmännyttiga ändamål.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Pensionsstiftelser</h3>
            <p>
              Bildas av arbetsgivare för att trygga pensionsutfästelser till anställda. Dessa regleras av 
              tryggandelagen snarare än stiftelselagen.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold mb-2">Vinstandelsstiftelser</h3>
            <p>
              Förvaltar vinstandelsmedel för anställdas räkning. Även dessa regleras av särskild lagstiftning.
            </p>
          </div>
          <div className="mt-4 text-right">
            <button onClick={scrollToTop} className="inline-flex items-center text-[#2563eb] hover:underline">
              <span className="mr-1">Tillbaka till toppen</span>
              <ArrowLeft className="w-4 h-4 transform rotate-90" />
            </button>
          </div>
        </section>
        
        <section id="juridiska-ramar" className="mb-8 scroll-mt-24">
          <h2 className="text-2xl font-semibold mb-3 text-[#A3C1DA]">Stiftelselagen och juridiska ramar</h2>
          <p className="mb-4">
            Stiftelselagen från 1994 utgör den primära rättsliga ramen för stiftelser i Sverige. Lagen definierar:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Hur en stiftelse bildas (genom förordnande och förmögenhetsöverföring)</li>
            <li>Krav på stiftelsens styrelse eller förvaltare</li>
            <li>Regler för förvaltning av stiftelsens egendom</li>
            <li>Bokföring och redovisningsskyldighet</li>
            <li>Tillsyn från länsstyrelserna</li>
            <li>Regler för ändring av föreskrifter och upplösning</li>
          </ul>
          <p className="mb-4">
            Utöver stiftelselagen påverkas stiftelser av flera andra lagar och regler:
          </p>
          <ul className="list-disc pl-6">
            <li>Bokföringslagen och årsredovisningslagen</li>
            <li>Skattelagstiftning (inkomstskattelagen)</li>
            <li>Tryggandelagen (för pensions- och personalstiftelser)</li>
            <li>Permutationslagen (för ändring av föreskrifter i äldre stiftelser)</li>
          </ul>
          <div className="mt-4 text-right">
            <button onClick={scrollToTop} className="inline-flex items-center text-[#2563eb] hover:underline">
              <span className="mr-1">Tillbaka till toppen</span>
              <ArrowLeft className="w-4 h-4 transform rotate-90" />
            </button>
          </div>
        </section>
        
        <section id="starta-stiftelse" className="mb-8 scroll-mt-24">
          <h2 className="text-2xl font-semibold mb-3 text-[#A3C1DA]">Att starta en stiftelse</h2>
          <p className="mb-4">
            Att bilda en stiftelse kräver noggrann planering och juridisk kunskap. Här är de grundläggande stegen:
          </p>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">1. Förordnande (stiftelseförklaring)</h3>
            <p>
              Upprätta ett skriftligt förordnande där du tydligt anger stiftelsens ändamål, hur den ska förvaltas, 
              och vilken egendom som avsätts. Detta dokument är stiftelsens grunddokument och måste vara juridiskt korrekt.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">2. Förmögenhetsöverföring</h3>
            <p>
              Överför den egendom (pengar, fastigheter, värdepapper etc.) som ska utgöra stiftelsens kapital. 
              En stiftelse bildas juridiskt först när både förordnande och förmögenhetsöverföring är genomförda.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">3. Utse styrelse eller förvaltare</h3>
            <p>
              En stiftelse kan ha egen styrelse (egen förvaltning) eller förvaltas av en juridisk person (anknuten förvaltning). 
              Styrelsen eller förvaltaren ansvarar för att stiftelsens ändamål uppfylls.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">4. Registrering</h3>
            <p>
              Stiftelser som bedriver näringsverksamhet måste registreras i stiftelseregistret hos Bolagsverket. 
              Andra stiftelser ska anmälas till länsstyrelsen för tillsyn om de är skyldiga att upprätta årsredovisning.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">5. Organisationsnummer</h3>
            <p>
              Ansök om organisationsnummer hos Skatteverket. Detta behövs för att stiftelsen ska kunna öppna bankkonto, 
              ingå avtal och hantera sin ekonomi.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold mb-2">6. Skatteregler och redovisning</h3>
            <p>
              Sätt upp rutiner för bokföring och redovisning. Undersök vilka skatteregler som gäller för din stiftelse. 
              Allmännyttiga stiftelser kan under vissa förutsättningar vara befriade från inkomstskatt.
            </p>
          </div>
          
          <p className="mt-4 italic">
            Hela processen att starta en stiftelse tar vanligtvis 3-6 månader, beroende på komplexitet och 
            handläggningstider hos myndigheter. Kostnaden varierar men inkluderar ofta juridisk rådgivning, 
            registreringsavgifter och det kapital som ska utgöra stiftelsens grundförmögenhet.
          </p>
          <div className="mt-4 text-right">
            <button onClick={scrollToTop} className="inline-flex items-center text-[#2563eb] hover:underline">
              <span className="mr-1">Tillbaka till toppen</span>
              <ArrowLeft className="w-4 h-4 transform rotate-90" />
            </button>
          </div>
        </section>
        
        <section id="soka-stiftelsemedel" className="mb-8 scroll-mt-24" itemScope itemType="https://schema.org/HowTo">
          <h2 className="text-2xl font-semibold mb-3 text-[#A3C1DA]" itemProp="name">Att söka stiftelsemedel</h2>
          <p className="mb-4" itemProp="description">
            Att söka bidrag från stiftelser kan vara en tidskrävande process, men med rätt strategi kan du öka dina chanser att lyckas.
            Här är vår kompletta guide till hur du effektivt söker stiftelsemedel:
          </p>
          
          <meta itemProp="totalTime" content="P1D" />
          <LazyImage
            itemProp="image"
            src="https://www.sokastiftelsemedel.se/images/stiftelser-guide.jpg"
            alt="Guide till att söka stiftelsemedel"
            className="hidden"
          />
          
          <div className="mb-6" itemScope itemProp="step" itemType="https://schema.org/HowToStep">
            <meta itemProp="position" content="1" />
            <h3 className="text-lg font-semibold mb-2 text-[#2563eb]" itemProp="name">Steg 1: Förberedelser innan ansökan</h3>
            
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Förstå stiftelsens ändamål</h4>
              <p>
                Läs noga igenom stiftelsens stadgar och ändamål. Ansök endast till stiftelser där ditt projekt eller din
                situation tydligt matchar stiftelsens syfte. Här kan <strong>SOKASTIFTELSEMEDEL.SE</strong> hjälpa dig att
                hitta stiftelser som matchar just dina behov genom vår avancerade sökfunktion.
              </p>
            </div>
            
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Kartlägg relevanta stiftelser</h4>
              <p>
                Skapa en lista över stiftelser som kan vara relevanta för ditt ändamål. Undersök:
              </p>
              <ul className="list-disc pl-6 mb-2">
                <li>Vilka belopp de vanligtvis delar ut</li>
                <li>Ansökningsperioder och deadlines</li>
                <li>Specifika krav och begränsningar</li>
                <li>Tidigare beviljade ansökningar (om informationen finns tillgänglig)</li>
              </ul>
            </div>
            
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Samla nödvändig dokumentation</h4>
              <p>
                Förbered alla dokument som kan behövas för ansökan:
              </p>
              <ul className="list-disc pl-6">
                <li>Personbevis eller organisationsnummer</li>
                <li>Intyg om studier eller anställning</li>
                <li>Projektbeskrivning eller forskningsplan</li>
                <li>Budget och finansieringsplan</li>
                <li>CV eller meritförteckning</li>
                <li>Rekommendationsbrev (om relevant)</li>
              </ul>
            </div>
          </div>
          
          <div className="mb-6" itemScope itemProp="step" itemType="https://schema.org/HowToStep">
            <meta itemProp="position" content="2" />
            <h3 className="text-lg font-semibold mb-2 text-[#2563eb]" itemProp="name">Steg 2: Utforma en övertygande ansökan</h3>
            
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Förbered en tydlig ansökan</h4>
              <p>
                Din ansökan bör vara koncis, välskriven och innehålla all information som stiftelsen efterfrågar.
                Var specifik med vad du söker medel till och hur det relaterar till stiftelsens ändamål.
              </p>
            </div>
            
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Strukturera din ansökan</h4>
              <p>En effektiv ansökan innehåller vanligtvis:</p>
              <ol className="list-decimal pl-6 mb-2">
                <li><strong>Introduktion:</strong> Kort presentation av dig själv eller din organisation</li>
                <li><strong>Syfte:</strong> Tydlig beskrivning av vad du söker medel till</li>
                <li><strong>Relevans:</strong> Förklaring av hur ditt syfte matchar stiftelsens ändamål</li>
                <li><strong>Budget:</strong> Specificerad budget med tydliga kostnader</li>
                <li><strong>Resultat:</strong> Beskrivning av förväntade resultat eller effekter</li>
                <li><strong>Kontaktuppgifter:</strong> Fullständiga och korrekta kontaktuppgifter</li>
              </ol>
            </div>
            
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Var realistisk med belopp</h4>
              <p>
                Undersök vilka belopp stiftelsen vanligtvis delar ut och anpassa din ansökan därefter.
                Att söka orealistiskt höga belopp kan minska dina chanser.
              </p>
            </div>
            
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Personlig ton</h4>
              <p>
                Även om ansökan ska vara formell, försök att förmedla din passion och ditt engagemang.
                Förklara varför projektet eller ändamålet är viktigt för dig personligen.
              </p>
            </div>
          </div>
          
          <div className="mb-6" itemScope itemProp="step" itemType="https://schema.org/HowToStep">
            <meta itemProp="position" content="3" />
            <h3 className="text-lg font-semibold mb-2 text-[#2563eb]" itemProp="name">Steg 3: Inlämning och uppföljning</h3>
            
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Respektera deadlines och formkrav</h4>
              <p>
                Många stiftelser har specifika ansökningsperioder och formkrav. Försent inkomna eller felaktigt
                utformade ansökningar avslås ofta utan bedömning.
              </p>
            </div>
            
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Skicka in ansökan i god tid</h4>
              <p>
                Lämna in din ansökan i god tid före deadline. Detta ger dig möjlighet att åtgärda eventuella problem
                eller komplettera med information om det skulle behövas.
              </p>
            </div>
            
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Följ upp din ansökan</h4>
              <p>
                Om stiftelsen tillåter det, följ upp din ansökan efter en rimlig tid (vanligtvis 2-4 veckor)
                för att bekräfta att den mottagits och är komplett.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2">Sök från flera källor</h4>
              <p>
                Förlita dig inte på en enda stiftelse. Sök från flera källor för att öka dina chanser.
                Med <strong>SOKASTIFTELSEMEDEL.SE</strong> kan du enkelt hitta och spara listor med relevanta stiftelser
                för dina behov, vilket sparar dig värdefull tid i sökprocessen.
              </p>
            </div>
          </div>
          
          <div className="bg-[#F8FAFC] p-4 rounded-lg border border-[#E2E8F0] mt-6">
            <h3 className="text-lg font-semibold mb-2 text-[#2563eb]">Tidslinje för ansökningsprocessen</h3>
            <ol className="relative border-l border-[#A3C1DA] ml-4 space-y-6 pt-2">
              <li className="ml-6">
                <span className="absolute flex items-center justify-center w-6 h-6 bg-[#A3C1DA] rounded-full -left-3 text-white text-sm">1</span>
                <h4 className="font-semibold">Förberedelse (2-4 veckor)</h4>
                <p className="text-sm text-gray-600">Researcha stiftelser, samla dokumentation, planera ansökan</p>
              </li>
              <li className="ml-6">
                <span className="absolute flex items-center justify-center w-6 h-6 bg-[#A3C1DA] rounded-full -left-3 text-white text-sm">2</span>
                <h4 className="font-semibold">Ansökan (1-2 veckor)</h4>
                <p className="text-sm text-gray-600">Skriv ansökan, sammanställ bilagor, kontrollera allt noga</p>
              </li>
              <li className="ml-6">
                <span className="absolute flex items-center justify-center w-6 h-6 bg-[#A3C1DA] rounded-full -left-3 text-white text-sm">3</span>
                <h4 className="font-semibold">Väntetid (1-6 månader)</h4>
                <p className="text-sm text-gray-600">Stiftelsens handläggningstid varierar kraftigt</p>
              </li>
              <li className="ml-6">
                <span className="absolute flex items-center justify-center w-6 h-6 bg-[#A3C1DA] rounded-full -left-3 text-white text-sm">4</span>
                <h4 className="font-semibold">Beslut</h4>
                <p className="text-sm text-gray-600">Beviljande eller avslag meddelas</p>
              </li>
              <li className="ml-6">
                <span className="absolute flex items-center justify-center w-6 h-6 bg-[#A3C1DA] rounded-full -left-3 text-white text-sm">5</span>
                <h4 className="font-semibold">Utbetalning (vid beviljande)</h4>
                <p className="text-sm text-gray-600">Vanligtvis inom 1-4 veckor efter beslut</p>
              </li>
            </ol>
          </div>
          <div className="mt-4 text-right">
            <button onClick={scrollToTop} className="inline-flex items-center text-[#2563eb] hover:underline">
              <span className="mr-1">Tillbaka till toppen</span>
              <ArrowLeft className="w-4 h-4 transform rotate-90" />
            </button>
          </div>
        </section>
        
        <section id="vanliga-misstag" className="mb-8 scroll-mt-24">
          <h2 className="text-2xl font-semibold mb-3 text-[#A3C1DA]">Vanliga misstag att undvika</h2>
          <p className="mb-4">
            Många ansökningar avslås på grund av enkla misstag som lätt kan undvikas. Här är de vanligaste fallgroparna:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-[#FEF2F2] p-4 rounded-lg border border-[#FCA5A5]">
              <h3 className="font-semibold mb-2 text-[#B91C1C]">Ansöker till fel stiftelse</h3>
              <p className="text-sm">
                Många söker till stiftelser vars ändamål inte matchar deras behov. Läs alltid stiftelsens stadgar noga.
              </p>
            </div>
            
            <div className="bg-[#FEF2F2] p-4 rounded-lg border border-[#FCA5A5]">
              <h3 className="font-semibold mb-2 text-[#B91C1C]">Missar deadlines</h3>
              <p className="text-sm">
                Försent inkomna ansökningar behandlas sällan. Håll koll på ansökningsperioder och deadlines.
              </p>
            </div>
            
            <div className="bg-[#FEF2F2] p-4 rounded-lg border border-[#FCA5A5]">
              <h3 className="font-semibold mb-2 text-[#B91C1C]">Ofullständig ansökan</h3>
              <p className="text-sm">
                Saknade uppgifter eller bilagor leder ofta till avslag. Följ stiftelsens instruktioner till punkt och pricka.
              </p>
            </div>
            
            <div className="bg-[#FEF2F2] p-4 rounded-lg border border-[#FCA5A5]">
              <h3 className="font-semibold mb-2 text-[#B91C1C]">För generell beskrivning</h3>
              <p className="text-sm">
                Vaga eller generella beskrivningar av ändamålet övertygar sällan. Var konkret och specifik.
              </p>
            </div>
            
            <div className="bg-[#FEF2F2] p-4 rounded-lg border border-[#FCA5A5]">
              <h3 className="font-semibold mb-2 text-[#B91C1C]">Orealistisk budget</h3>
              <p className="text-sm">
                Att söka för mycket eller ha en dåligt underbyggd budget minskar trovärdigheten.
              </p>
            </div>
            
            <div className="bg-[#FEF2F2] p-4 rounded-lg border border-[#FCA5A5]">
              <h3 className="font-semibold mb-2 text-[#B91C1C]">Språkliga brister</h3>
              <p className="text-sm">
                Stavfel, grammatikfel och dålig struktur ger ett oprofessionellt intryck. Låt någon annan läsa igenom din ansökan.
              </p>
            </div>
          </div>
          
          <div className="bg-[#F0FDF4] p-4 rounded-lg border border-[#86EFAC]">
            <h3 className="font-semibold mb-2 text-[#166534]">Tips för att undvika misstagen</h3>
            <ul className="list-disc pl-6">
              <li>Använd en checklista för att säkerställa att alla delar av ansökan är kompletta</li>
              <li>Börja i god tid för att undvika stress och misstag i sista minuten</li>
              <li>Låt någon annan läsa igenom din ansökan för att fånga upp eventuella brister</li>
              <li>Spara kopior av alla inskickade dokument</li>
              <li>Använd <strong>SOKASTIFTELSEMEDEL.SE</strong> för att hitta stiftelser som verkligen matchar dina behov</li>
            </ul>
          </div>
          <div className="mt-4 text-right">
            <button onClick={scrollToTop} className="inline-flex items-center text-[#2563eb] hover:underline">
              <span className="mr-1">Tillbaka till toppen</span>
              <ArrowLeft className="w-4 h-4 transform rotate-90" />
            </button>
          </div>
        </section>
        
        <section id="framgangsrika-ansokningar" className="mb-8 scroll-mt-24">
          <h2 className="text-2xl font-semibold mb-3 text-[#A3C1DA]">Framgångsrika ansökningar</h2>
          <p className="mb-4">
            Här är några exempel på framgångsrika ansökningar som kan ge dig inspiration:
          </p>
          
          <div className="bg-white shadow-md rounded-lg overflow-hidden mb-4">
            <div className="bg-[#A3C1DA] px-4 py-2">
              <h3 className="font-semibold text-white">Exempel 1: Forskningsstipendium</h3>
            </div>
            <div className="p-4">
              <p className="italic text-sm text-gray-600 mb-2">"Jag sökte 75 000 kr från Stiftelsen för Vetenskaplig Forskning för mitt forskningsprojekt om hållbara energilösningar."</p>
              <h4 className="font-semibold mb-1">Framgångsfaktorer:</h4>
              <ul className="list-disc pl-6 text-sm">
                <li>Tydlig koppling mellan forskningsprojektet och stiftelsens ändamål</li>
                <li>Detaljerad forskningsplan med tydliga milstolpar</li>
                <li>Realistisk budget med specificerade kostnader</li>
                <li>Tidigare meriter inom forskningsområdet väl dokumenterade</li>
                <li>Tydlig beskrivning av förväntade resultat och samhällsnytta</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white shadow-md rounded-lg overflow-hidden mb-4">
            <div className="bg-[#A3C1DA] px-4 py-2">
              <h3 className="font-semibold text-white">Exempel 2: Utbildningsstipendium</h3>
            </div>
            <div className="p-4">
              <p className="italic text-sm text-gray-600 mb-2">"Jag beviljades 30 000 kr från Stiftelsen för Utbildningsfrämjande för att finansiera min masterutbildning i internationell rätt."</p>
              <h4 className="font-semibold mb-1">Framgångsfaktorer:</h4>
              <ul className="list-disc pl-6 text-sm">
                <li>Personlig berättelse om varför utbildningen var viktig</li>
                <li>Tydlig koppling till stiftelsens fokus på utbildning</li>
                <li>Konkreta karriärmål efter utbildningen</li>
                <li>Intyg från universitetet och tidigare studieresultat</li>
                <li>Detaljerad budget för studiekostnader</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
            <div className="bg-[#A3C1DA] px-4 py-2">
              <h3 className="font-semibold text-white">Exempel 3: Projektbidrag för ideell förening</h3>
            </div>
            <div className="p-4">
              <p className="italic text-sm text-gray-600 mb-2">"Vår förening fick 120 000 kr från Stiftelsen för Social Utveckling för att starta ett mentorsprogram för ungdomar i utsatta områden."</p>
              <h4 className="font-semibold mb-1">Framgångsfaktorer:</h4>
              <ul className="list-disc pl-6 text-sm">
                <li>Väldefinierat projekt med tydliga mål och målgrupp</li>
                <li>Detaljerad implementeringsplan med tidsramar</li>
                <li>Tidigare erfarenhet av liknande projekt</li>
                <li>Plan för utvärdering och uppföljning</li>
                <li>Strategi för långsiktig hållbarhet efter projektets slut</li>
                <li>Samarbete med etablerade organisationer i området</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-[#F8FAFC] p-5 rounded-lg border border-[#E2E8F0] mb-4 overflow-x-auto">
            <h3 className="text-lg font-semibold mb-3 text-[#2563eb]">Mall för stiftelseansökan</h3>
            <div className="mb-4 bg-[#DBEAFE] p-3 rounded-lg border border-[#93C5FD]">
              <p className="text-sm mb-2">
                <strong>Viktigt att veta:</strong> Denna mall är avsedd för fristående ansökningar via e-post eller brev till stiftelser.
                Många stiftelser har sina egna specifika ansökningsformulär på sina webbplatser, och att hitta dessa är extremt tidskrävande.
              </p>
              <p className="text-sm mb-2">
                Med <strong>SOKASTIFTELSEMEDEL.SE</strong> kan du enkelt hitta rätt stiftelser och deras ansökningsformulär,
                vilket kan spara dig veckor av arbete varje gång du vill göra ansökningar.
              </p>
              <p className="text-sm">
                <strong>Alla dessa mallar och mycket mer finns tillgängliga i vår "Ansökningshjälpen AI"-sida!</strong> Med våra verktyg kan du:
              </p>
              <ul className="text-sm list-disc pl-5 mt-1">
                <li>Direkt se vilka stiftelser som har webbansökningar och vilka som tar emot ansökningar via e-post</li>
                <li>Extrahera e-postadresser och skicka AI-genererade ansökningar i bulk direkt från vår webbplats</li>
                <li>Få specifika ansökningstexter anpassade för specifika stiftelser</li>
                <li>Växla mellan att endast skicka e-postansökningar och att få hjälp med webbansökningar</li>
              </ul>
            </div>
            <p className="mb-3 text-sm text-gray-600">
              Här är en generell mall som du kan anpassa för din ansökan när stiftelsen inte har ett eget formulär. Kom ihåg att alltid följa stiftelsens specifika instruktioner.
            </p>
            
            <div className="bg-white p-4 rounded border border-gray-200 min-w-[320px]">
              <div className="mb-4 pb-3 border-b border-gray-200">
                <h4 className="font-bold text-gray-800">ANSÖKAN OM MEDEL FRÅN [STIFTELSENS NAMN]</h4>
                <p className="text-sm text-gray-500">Datum: [Dagens datum]</p>
              </div>
              
              <div className="mb-4">
                <h5 className="font-semibold mb-1">1. SÖKANDE</h5>
                <ul className="text-sm space-y-1">
                  <li><span className="font-medium">Namn:</span> [Ditt fullständiga namn]</li>
                  <li><span className="font-medium">Personnummer:</span> [Ditt personnummer]</li>
                  <li><span className="font-medium">Adress:</span> [Din fullständiga adress]</li>
                  <li><span className="font-medium">Telefon:</span> [Ditt telefonnummer]</li>
                  <li><span className="font-medium">E-post:</span> [Din e-postadress]</li>
                </ul>
              </div>
              
              <div className="mb-4">
                <h5 className="font-semibold mb-1">2. ANSÖKAN AVSER</h5>
                <p className="text-sm">
                  [Kort och tydlig beskrivning av vad du söker medel till. Max 2-3 meningar.]
                </p>
              </div>
              
              <div className="mb-4">
                <h5 className="font-semibold mb-1">3. SÖKT BELOPP</h5>
                <p className="text-sm">[Belopp] SEK</p>
              </div>
              
              <div className="mb-4">
                <h5 className="font-semibold mb-1">4. BESKRIVNING AV ÄNDAMÅLET</h5>
                <p className="text-sm">
                  [Detaljerad beskrivning av ändamålet. Förklara tydligt vad medlen ska användas till och varför det är viktigt.
                  Koppla till stiftelsens ändamål. 10-15 meningar.]
                </p>
              </div>
              
              <div className="mb-4">
                <h5 className="font-semibold mb-1">5. BUDGET</h5>
                <div className="text-sm">
                  <p className="mb-1">Specificerad budget för ändamålet:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>[Kostnadspost 1]: [Belopp] SEK</li>
                    <li>[Kostnadspost 2]: [Belopp] SEK</li>
                    <li>[Kostnadspost 3]: [Belopp] SEK</li>
                    <li>Totalt: [Totalbelopp] SEK</li>
                  </ul>
                </div>
              </div>
              
              <div className="mb-4">
                <h5 className="font-semibold mb-1">6. TIDSPLAN</h5>
                <p className="text-sm">
                  [Beskrivning av när och hur medlen kommer att användas. Inkludera start- och slutdatum om relevant.]
                </p>
              </div>
              
              <div className="mb-4">
                <h5 className="font-semibold mb-1">7. FÖRVÄNTADE RESULTAT</h5>
                <p className="text-sm">
                  [Beskrivning av vilka resultat som förväntas och hur de kommer att mätas eller utvärderas.]
                </p>
              </div>
              
              <div className="mb-4">
                <h5 className="font-semibold mb-1">8. BILAGOR</h5>
                <ul className="text-sm list-disc pl-5">
                  <li>Personbevis</li>
                  <li>CV/Meritförteckning</li>
                  <li>[Andra relevanta bilagor, t.ex. intyg, rekommendationsbrev, etc.]</li>
                </ul>
              </div>
              
              <div className="mt-5 pt-3 border-t border-gray-200">
                <p className="text-sm">
                  Jag försäkrar att de uppgifter som lämnats i denna ansökan är korrekta och fullständiga.
                </p>
                <div className="mt-3">
                  <p className="text-sm">[Ort och datum]</p>
                  <p className="mt-4 text-sm">[Ditt namn]</p>
                  <p className="text-sm">[Underskrift]</p>
                </div>
              </div>
            </div>
            
            <p className="mt-3 text-sm text-gray-600">
              <strong>Tips:</strong> Anpassa denna mall efter stiftelsens specifika krav och din situation. Var konkret,
              relevant och tydlig i alla delar av ansökan.
            </p>
          </div>
          
          <div className="bg-[#F8FAFC] p-5 rounded-lg border border-[#E2E8F0] mb-4 overflow-x-auto">
            <h3 className="text-lg font-semibold mb-3 text-[#2563eb]">Exempel på stiftelsespecifikt ansökningsformulär</h3>
            <p className="mb-3 text-sm text-gray-600">
              Många stiftelser har egna ansökningsformulär som ser ut ungefär så här:
            </p>
            
            <div className="bg-white p-4 rounded border border-gray-200 min-w-[320px]">
              <div className="mb-4 pb-3 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h4 className="font-bold text-gray-800">STIFTELSEN UTBILDNINGSFRÄMJANDET</h4>
                  <p className="text-xs text-gray-500">Ansökningsformulär för stipendium 2025</p>
                </div>
                <div className="text-xs text-gray-500 text-right">
                  <p>Ansökningsperiod:</p>
                  <p className="font-semibold">1 mars - 15 april 2025</p>
                </div>
              </div>
              
              <div className="space-y-4 text-sm">
                <div className="border border-gray-200 rounded p-3">
                  <label className="font-semibold block mb-1">1. Personuppgifter <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500">Förnamn</label>
                      <div className="h-6 bg-gray-100 rounded w-full"></div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">Efternamn</label>
                      <div className="h-6 bg-gray-100 rounded w-full"></div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">Personnummer (ÅÅMMDD-XXXX)</label>
                      <div className="h-6 bg-gray-100 rounded w-full"></div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">Telefon</label>
                      <div className="h-6 bg-gray-100 rounded w-full"></div>
                    </div>
                  </div>
                </div>
                
                <div className="border border-gray-200 rounded p-3">
                  <label className="font-semibold block mb-1">2. Utbildning <span className="text-red-500">*</span></label>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-500">Lärosäte</label>
                      <div className="h-6 bg-gray-100 rounded w-full"></div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">Program/kurs</label>
                      <div className="h-6 bg-gray-100 rounded w-full"></div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">Studieperiod</label>
                      <div className="h-6 bg-gray-100 rounded w-full"></div>
                    </div>
                  </div>
                </div>
                
                <div className="border border-gray-200 rounded p-3">
                  <label className="font-semibold block mb-1">3. Sökt belopp och ändamål <span className="text-red-500">*</span></label>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-500">Belopp (SEK)</label>
                      <div className="h-6 bg-gray-100 rounded w-1/3"></div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">Beskrivning av ändamål</label>
                      <div className="h-20 bg-gray-100 rounded w-full"></div>
                    </div>
                  </div>
                </div>
                
                <div className="border border-gray-200 rounded p-3">
                  <label className="font-semibold block mb-1">4. Bilagor <span className="text-red-500">*</span></label>
                  <div className="space-y-1">
                    <div className="flex items-center">
                      <div className="w-4 h-4 border border-gray-400 rounded mr-2"></div>
                      <span>Personbevis (obligatoriskt)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 border border-gray-400 rounded mr-2"></div>
                      <span>Studieintyg (obligatoriskt)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 border border-gray-400 rounded mr-2"></div>
                      <span>CV</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 border border-gray-400 rounded mr-2"></div>
                      <span>Rekommendationsbrev</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-5 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">
                  Genom att skicka in detta formulär intygar jag att all information är korrekt och fullständig.
                </p>
                <button className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm font-medium">Skicka ansökan</button>
              </div>
            </div>
            
            <div className="mt-4 bg-[#ECFDF5] p-3 rounded-lg border border-[#6EE7B7]">
              <h4 className="font-semibold text-[#065F46] mb-2">Hur SOKASTIFTELSEMEDEL.SE hjälper dig med ansökningsformulär</h4>
              <p className="text-sm mb-2">
                Att hitta och fylla i olika stiftelsers ansökningsformulär kan vara mycket tidskrävande. Med vår tjänst får du:
              </p>
              <ul className="text-sm list-disc pl-5">
                <li>Direktlänkar till stiftelsers ansökningsformulär</li>
                <li>AI-genererade svar anpassade för varje specifik stiftelse</li>
                <li>Automatisk identifiering av vilka stiftelser som matchar dina behov</li>
                <li>Hjälp att fylla i både online-formulär och e-postansökningar</li>
                <li>Möjlighet att spara dina uppgifter för att snabbt kunna återanvända dem i flera ansökningar</li>
              </ul>
              <p className="text-sm mt-2">
                Detta kan spara dig veckor av arbete och öka dina chanser att få din ansökan beviljad.
              </p>
            </div>
          </div>
        </section>
        
        <section id="sokastiftelsemedel" className="mb-8 scroll-mt-24">
          <h2 className="text-2xl font-semibold mb-3 text-[#A3C1DA]">Hur SOKASTIFTELSEMEDEL.SE kan hjälpa dig</h2>
          <p className="mb-4">
            Att navigera i stiftelsevärlden kan vara utmanande och tidskrävande. Vår plattform erbjuder flera 
            innovativa verktyg som gör processen enklare, oavsett om du är en privatperson, organisation, företag eller myndighet:
          </p>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Avancerad sökning med AI-stöd</h3>
            <p>
              Vår sökmotor använder avancerad AI-teknik för att matcha dina behov med rätt stiftelser. 
              Istället för att manuellt gå igenom tusentals stiftelser kan du snabbt hitta de som är relevanta för just dig.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Omfattande databas</h3>
            <p>
              Vi har samlat information om över 17 000 svenska stiftelser i en sökbar databas. 
              Informationen uppdateras regelbundet för att säkerställa att du har tillgång till aktuella uppgifter.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Specialiserade verktyg för olika användare</h3>
            <p>
              Oavsett om du är en privatperson som söker stipendier, en organisation som behöver projektfinansiering, 
              eller en stiftelse som vill hitta lämpliga mottagare, har vi verktyg anpassade för dina behov.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Tidsbesparande funktioner</h3>
            <p>
              Våra premium-verktyg kan spara dig veckor av research genom automatiserad sökning, 
              analys och matchning. Vi använder webbskrapning och djupgående AI-analys för att hitta information 
              som annars skulle kräva timmar av manuellt arbete.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold mb-2">Vägledning genom hela processen</h3>
            <p>
              Från att hitta rätt stiftelser till att förbereda din ansökan – vi erbjuder vägledning genom hela processen. 
              Vårt mål är att demokratisera tillgången till stiftelsemedel genom att göra informationen tillgänglig och begriplig för alla.
            </p>
          </div>
        </section>
        
        <section id="framtiden" className="mb-8 scroll-mt-24">
          <h2 className="text-2xl font-semibold mb-3 text-[#A3C1DA]">Framtiden för stiftelser i Sverige</h2>
          <p className="mb-4">
            Stiftelser fortsätter att spela en viktig roll i det svenska samhället, men sektorn står inför flera utmaningar och möjligheter:
          </p>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Digitalisering och transparens</h3>
            <p>
              Ökade krav på transparens och digitalisering förändrar hur stiftelser arbetar och kommunicerar. 
              Plattformar som <strong>SOKASTIFTELSEMEDEL.SE</strong> bidrar till denna utveckling genom att göra 
              information mer tillgänglig.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Hållbarhet och impact investing</h3>
            <p>
              Allt fler stiftelser integrerar hållbarhetsperspektiv i sin kapitalförvaltning och bidragsgivning. 
              "Impact investing" – investeringar som syftar till både finansiell avkastning och positiv samhällspåverkan – blir vanligare.
            </p>
          </div>
          
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Samverkan mellan sektorer</h3>
            <p>
              Gränserna mellan offentlig sektor, näringsliv och civilsamhälle blir allt mer flytande. 
              Stiftelser fungerar ofta som brobyggare och katalysatorer för samverkan mellan dessa sektorer.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold mb-2">Nya finansieringsmodeller</h3>
            <p>
              Innovativa finansieringsmodeller som sociala utfallskontrakt och blandfinansiering (där stiftelser 
              samarbetar med andra finansiärer) blir allt vanligare och skapar nya möjligheter för samhällsförändring.
            </p>
          </div>
        </section>
        
        <div className="bg-[#F2F7FF] p-4 rounded-lg border border-[#A3C1DA] mb-8">
          <h2 className="text-lg font-semibold mb-2 text-[#2563eb]">Behöver du hjälp med stiftelserelaterade frågor?</h2>
          <p className="mb-3">
            SOKASTIFTELSEMEDEL.SE erbjuder både kostnadsfria verktyg och premiumtjänster för att hjälpa dig navigera i stiftelsevärlden.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/" className="bg-[#A3C1DA] text-white px-4 py-2 rounded hover:bg-[#7A98B0] transition-colors text-center">
              Sök bland stiftelser
            </Link>
            <Link to="/premium" className="bg-[#F2C1D1] text-white px-4 py-2 rounded hover:bg-[#D1A3B3] transition-colors text-center">
              Utforska våra premiumtjänster
            </Link>
          </div>
        </div>
        <section id="vanliga-fragor" className="mb-8 scroll-mt-24">
          <h2 className="text-2xl font-semibold mb-3 text-[#A3C1DA]">Vanliga frågor (FAQ)</h2>
          <p className="mb-4">
            Här besvarar vi de vanligaste frågorna om att söka stiftelsemedel:
          </p>
          
          <div className="space-y-4">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-[#F8FAFC] px-4 py-3 border-l-4 border-[#A3C1DA]">
                <h3 className="font-semibold">Hur söker man stiftelsekapital?</h3>
              </div>
              <div className="p-4">
                <p>
                  För att söka stiftelsekapital behöver du först identifiera stiftelser vars ändamål matchar ditt behov.
                  Använd SOKASTIFTELSEMEDEL.SE för att hitta relevanta stiftelser. Läs sedan noga igenom stiftelsens stadgar
                  och ansökningskrav. Förbered en tydlig ansökan som beskriver ditt syfte, hur det matchar stiftelsens ändamål,
                  och en detaljerad budget. Skicka in ansökan enligt stiftelsens instruktioner och inom angiven deadline.
                </p>
              </div>
            </div>
            
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-[#F8FAFC] px-4 py-3 border-l-4 border-[#A3C1DA]">
                <h3 className="font-semibold">Kan man söka stiftelsekapital som privatperson?</h3>
              </div>
              <div className="p-4">
                <p>
                  Ja, många stiftelser riktar sig specifikt till privatpersoner. Det finns stiftelser som stödjer allt från
                  studier och forskning till kulturella projekt och sociala behov. Som privatperson kan du söka medel för
                  exempelvis utbildning, forskning, kulturella projekt, eller vid ekonomiska svårigheter. Avgörande är att
                  ditt behov matchar stiftelsens ändamål, vilket framgår av stiftelsens stadgar.
                </p>
              </div>
            </div>
            
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-[#F8FAFC] px-4 py-3 border-l-4 border-[#A3C1DA]">
                <h3 className="font-semibold">Vad krävs för att söka stiftelsemedel?</h3>
              </div>
              <div className="p-4">
                <p>
                  För att söka stiftelsemedel krävs vanligtvis:
                </p>
                <ul className="list-disc pl-6 mt-2">
                  <li>Att du uppfyller stiftelsens kriterier (ålder, bostadsort, studieinriktning, etc.)</li>
                  <li>En komplett ansökan enligt stiftelsens instruktioner</li>
                  <li>Relevanta bilagor som personbevis, intyg, projektbeskrivning, budget</li>
                  <li>Att du ansöker inom angiven ansökningsperiod</li>
                  <li>En tydlig koppling mellan ditt behov och stiftelsens ändamål</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-[#F8FAFC] px-4 py-3 border-l-4 border-[#A3C1DA]">
                <h3 className="font-semibold">Hur lång tid tar det att få svar på en stiftelseansökan?</h3>
              </div>
              <div className="p-4">
                <p>
                  Handläggningstiden varierar kraftigt mellan olika stiftelser. Mindre stiftelser kan ge besked inom några
                  veckor, medan större stiftelser med många ansökningar kan ta 3-6 månader eller längre. Vissa stiftelser
                  har fasta utdelningstillfällen, exempelvis en eller två gånger per år, vilket påverkar när du kan förvänta
                  dig svar. Stiftelsens hemsida eller ansökningsinformation anger ofta ungefärlig handläggningstid.
                </p>
              </div>
            </div>
            
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-[#F8FAFC] px-4 py-3 border-l-4 border-[#A3C1DA]">
                <h3 className="font-semibold">Måste man betala tillbaka stiftelsemedel?</h3>
              </div>
              <div className="p-4">
                <p>
                  Nej, stiftelsemedel är vanligtvis bidrag eller stipendier som inte behöver återbetalas. Till skillnad från
                  lån är stiftelsemedel gåvor utan återbetalningskrav. Däremot kan stiftelsen ställa krav på hur medlen
                  används och att du redovisar hur pengarna har använts. Om medlen inte används enligt överenskommelse kan
                  stiftelsen i vissa fall kräva återbetalning.
                </p>
              </div>
            </div>
            
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-[#F8FAFC] px-4 py-3 border-l-4 border-[#A3C1DA]">
                <h3 className="font-semibold">Är stiftelsemedel skattepliktiga?</h3>
              </div>
              <div className="p-4">
                <p>
                  Det beror på stiftelsens ändamål och hur medlen används. Generellt gäller att stipendier för utbildning
                  och forskning ofta är skattefria, medan bidrag för andra ändamål kan vara skattepliktiga. Stipendier för
                  att täcka levnadskostnader är vanligtvis skattepliktiga. Kontakta Skatteverket för specifik information
                  om din situation, eller konsultera en skatterådgivare.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 text-right">
            <button onClick={scrollToTop} className="inline-flex items-center text-[#2563eb] hover:underline">
              <span className="mr-1">Tillbaka till toppen</span>
              <ArrowLeft className="w-4 h-4 transform rotate-90" />
            </button>
          </div>
        </section>
        
        <div className="mt-8 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">Senast uppdaterad: 2 april 2025</p>
          <div className="mt-4">
            <Link to="/" className="text-[#2563eb] hover:underline flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Tillbaka till startsidan
            </Link>
          </div>
        </div>
      </article>
    </main>
  );
}